import { isAfter, isBefore } from 'date-fns';
import { ITableColumn } from '~app/dashboard/components/table/table.component';
import { DropdownOptions, IOption } from '~app/shared/interfaces/dropdown-options.interface';
import { StepperAssignmentType } from '../../types/stepper-assignment-type.type';

export function dateStringFormat(date: Date): string {
  return date
    ? date
        .toISOString()
        .substring(0, 10)
        .replace(/-/g, '')
    : undefined;
}

export function parseDateFromMW(date: string): Date {
  if (date.length !== 8) throw new Error('Date should be 8 characters long');
  const year = parseInt(date.substring(0, 4), 10);
  const month = parseInt(date.substring(4, 6), 10) - 1;
  const day = parseInt(date.substring(6, 8), 10);
  return new Date(year, month, day);
}

export function getTimestamp(date: Date, flag = false): string {
  const yyyy = date.getFullYear();
  const MM = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const HH = pad(date.getHours());
  const mm = pad(date.getMinutes());
  const ss = pad(date.getSeconds());
  if (flag) {
    return `${yyyy}${MM}${dd}T${HH}${mm}`;
  } else {
    return `${yyyy}${MM}${dd}T${HH}${mm}${ss}`;
  }
}

export function pad(n: number): string {
  if (n < 10) return `0${n}`;
  return n.toString();
}

export function parseBlobToString(data: Blob): Promise<string | ArrayBuffer> {
  return new Promise(resolve => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.readAsText(data);
  });
}

export function titleCase(input: string): string {
  const replace = (str: string, selector: string) =>
    str
      .split(selector)
      .map(w => {
        if (w[0]) {
          return w[0].toUpperCase() + w.substr(1);
        }
        return w;
      })
      .join(selector);

  input = input.toLowerCase();
  input = replace(input, ' ');
  input = replace(input, '@');
  input = replace(input, '-');
  input = replace(input, '_');
  input = replace(input, '/');
  return input;
}

export function sortString(a: string, b: string): number {
  const x = a.toLowerCase();
  const y = b.toLowerCase();
  if (x < y) return -1;
  if (x > y) return 1;
  return 0;
}

export function sortNumber(a: number, b: number): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

export function dirtyStringCompare(original: string, expected: string): boolean {
  try {
    return original.trim().toLowerCase() === expected;
  } catch (error) {
    return false;
  }
}

export function arrayToObject<T, Z>(
  arr: Array<T>,
  key: (item?: T, i?: number) => string,
  val: (item?: T, i?: number) => Z,
): {
  [key: string]: Z;
} {
  return arr.reduce((obj: any, item, i) => {
    obj[key(item, i)] = val(item, i);
    return obj;
  }, {});
}

export function enumEntries(a: { [key: string]: any }): Array<[string, string]> {
  const isEnumWithValue = a[0] !== undefined;
  let entries = Object.entries(a);
  if (isEnumWithValue) {
    entries = entries.filter(v => isNaN(parseInt(v[0], 10)));
  }
  return entries;
}

export function scrollToError(): void {
  setTimeout(() => {
    // TO DO: Find a better way to retrieve the proper element
    let el = document.querySelector('.error-list__item') || document.querySelector('.errors__item');
    if (!el) return;
    if (el.parentElement) el = el.parentElement;
    if (el.parentElement) el = el.parentElement;
    if (el.parentElement) el = el.parentElement;
    if (el.parentElement) el = el.parentElement;
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, 100);
}

export function sortDate(a: Date, b: Date): number {
  if (isBefore(a, b)) return -1;
  if (isAfter(a, b)) return 1;
  return 0;
}

export function createFile(data: Blob, fileName: string, type: string): File {
  try {
    return new File([data], fileName, { type });
  } catch (error) {
    // Workaround for IE
    const b = new Blob([data], { type }) as any;
    b.lastModifiedDate = new Date();
    b.name = fileName;
    return b;
  }
}

export function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

export function isCheckin(val: StepperAssignmentType): boolean {
  return val === 'check-in';
}

export function searchByColLabelName(label: string, cols: ITableColumn[]): ITableColumn {
  return cols.find(col => col.label === label);
}

export function moveElementInArray(arr: any[], fromIndex: number, toIndex = 0): void {
  const element = arr[fromIndex];
  arr.splice(fromIndex, 1);
  arr.splice(toIndex, 0, element);
}

export function sortArrayAlphabeticallyBy(array: Array<any>, orderBy: string): Array<any> {
  return array.sort((a, b) => a[orderBy].localeCompare(b[orderBy]));
}

export function arrangeOptionsOnTop(selectionList: DropdownOptions, topValues: string[]): IOption[] {
  return [
    ...selectionList
      .filter(x => topValues.includes(x.value))
      .sort((a, b) => topValues.indexOf(a.value) - topValues.indexOf(b.value)),
    ...selectionList.filter(x => !topValues.includes(x.value)),
  ];
}
